import React from 'react'

import { IonGrid, IonRow } from '@ionic/react'
import classnames from 'classnames'

import { PlaylistItem, formatTimeMS, PlayerStatus, Collection } from 'mytours-read-common'
import { Button, Spinner, Text } from '@mytours/components'

import { formatPlayPauseLabel } from '../../functions'
import SyncedTranscript from '../SyncedTranscript'
import AudioSeekBar from './AudioSeekBar'
import { getAdjacentAudioItem, seekToPercentage, switchTrack, DEFAULT_SKIP_SECONDS } from '../../soundController'
import { getStyleProperty, isRTL } from '../../../platform'
import strings from '../../../../strings'

import { ImageSizePreset } from '../../../../common_lib/types'
import { generateImageSrcSet, generateImageUrl } from '../../../../common_lib/utils'

import './styles/style.less'
import { MediaItem } from 'mytours-read-api-typescript'
import { getImageAlt } from 'mobile_web/modules/common/functions'

interface Props {
	currentAudio: PlaylistItem
	onPlayPause: () => void
	onBack: () => void
	onForward: () => void
	collection: Collection
}

interface State {
	coverImageItem: MediaItem
	maxImageHeight: number
	showTranscriptModal: boolean
}

/**
 * The initial state for our internal component state.
 */
const INITIAL_STATE: State = {
	coverImageItem: null,
	maxImageHeight: 0,
	showTranscriptModal: false,
}

export default class FullScreenAudioPlayer extends React.Component<Props, State> {
	state = INITIAL_STATE
	_imageContainer: HTMLIonRowElement

	constructor(props: Props) {
		super(props)
		this._imageContainer = null
	}

	componentDidMount = () => {
		this.setupCoverImage()

		addEventListener('resize', this.updateMaxImageSize)
	}

	componentDidUpdate = (prevProps: Props) => {
		this.onCurrentTimeUpdate(prevProps.currentAudio.currentTime)
		this.updateMaxImageSize()
	}

	componentWillUnmount = () => {
		removeEventListener('resize', this.updateMaxImageSize)
	}

	private openTranscriptModal = () => this.setState({ showTranscriptModal: true })
	private closeTranscriptModal = () => this.setState({ showTranscriptModal: false })

	private updateMaxImageSize = (): void => {
		if (!this._imageContainer) return

		const { clientHeight } = this._imageContainer
		const { maxImageHeight } = this.state

		// We only use `maxImageHeight` to control the image display. It could be a boolean flag, really.
		if (clientHeight > 0 && maxImageHeight !== clientHeight) {
			this.setState({ maxImageHeight: clientHeight }, this.setupCoverImage)
		}
	}

	private onCurrentTimeUpdate = (prevCurrentTime: number): void => {
		const {
			currentAudio: { currentTime },
		} = this.props
		if (prevCurrentTime !== currentTime) {
			this.setupCoverImage()
		}
	}

	private setupCoverImage = (): void => {
		const {
			currentAudio: { currentTime, audioTrack },
		} = this.props
		const { coverImages: dataCoverImages = [], startTimes: dataStartTimes = [], audio } = audioTrack || {}

		const startTimes = {}

		const coverImagesReverse = [...dataCoverImages]
		const startTimesReverse = [...dataStartTimes]

		// Default to the audio's thumbnail (or the first cover image as a fallback)
		// so the player renders an image immediately on mount. Without this the player
		// shows an empty image area until currentTime advances past the first start
		// time, which is only after the user hits play.
		let thumbnailMediaItem = audio.thumbnail_media_item || coverImagesReverse[0]

		for (let i = 0; i < dataStartTimes.length; i++) {
			if (coverImagesReverse[i]) {
				// {i + 1} - skip first item, because it's audio file with time `0`. we don't need if
				startTimes[startTimesReverse[i + 1]] = coverImagesReverse[i]
			}
		}

		for (const time of Object.keys(startTimes)) {
			if (currentTime > parseFloat(time) && startTimes?.[time]) {
				thumbnailMediaItem = startTimes[time]
			}
		}

		this.setState({ coverImageItem: thumbnailMediaItem })
	}

	onPrevTrack = (): void => switchTrack(this.props.currentAudio, -1)
	onNextTrack = (): void => switchTrack(this.props.currentAudio, 1)

	getBackButton = (): JSX.Element => (
		<Button
			variant="clear"
			className="time-controls-btn"
			onClick={this.props.onBack}
			aria-label={
				strings.formatString(strings.accessibility_seek_backward, { seconds: DEFAULT_SKIP_SECONDS }) as string
			}
		>
			<i
				className="fak fa-lg fa-skip-back-10"
				aria-label={
					strings.formatString(strings.accessibility_seek_backward, { seconds: DEFAULT_SKIP_SECONDS }) as string
				}
			/>
		</Button>
	)

	getForwardButton = (): JSX.Element => (
		<Button
			variant="clear"
			className="time-controls-btn"
			onClick={this.props.onForward}
			aria-label={strings.formatString(strings.accessibility_seek_forward, { seconds: DEFAULT_SKIP_SECONDS }) as string}
		>
			<i
				className="fak fa-lg fa-skip-forward-10"
				aria-label={
					strings.formatString(strings.accessibility_seek_forward, { seconds: DEFAULT_SKIP_SECONDS }) as string
				}
			/>
		</Button>
	)

	getPlayPauseButton = (): JSX.Element => {
		const { currentAudio, onPlayPause } = this.props
		const { playerStatus, audioTrack } = currentAudio || {}
		const isPlaying = playerStatus === PlayerStatus.Playing
		const title = audioTrack?.audio?.title

		return (
			<Button
				variant="clear"
				className="play-pause-btn"
				onClick={onPlayPause}
				aria-label={formatPlayPauseLabel(isPlaying, title)}
				// `ariaLabel` won't change unless we remount
				key={formatPlayPauseLabel(isPlaying, title)}
			>
				<i className={`fas fa-${isPlaying ? 'pause' : 'play'}`} />
			</Button>
		)
	}

	getTrackStepButton = (direction: -1 | 1, enabled: boolean): JSX.Element => {
		const icon = direction < 0 ? 'fas fa-step-backward' : 'fas fa-step-forward'
		const onClick = direction < 0 ? this.onPrevTrack : this.onNextTrack
		const ariaLabel = direction < 0 ? strings.tour_previous : strings.tour_next
		return (
			<Button
				variant="clear"
				className={classnames('time-controls-btn', !enabled && 'disabled')}
				onClick={onClick}
				aria-label={ariaLabel}
				disabled={!enabled}
			>
				<i className={`${icon} fa-lg`} aria-label={ariaLabel} />
			</Button>
		)
	}

	onImageRef = (ref: HTMLIonRowElement) => {
		setTimeout(() => {
			this._imageContainer = ref
			this.updateMaxImageSize()
		}, 0)
	}

	render() {
		const {
			currentAudio: { playerStatus, currentTime, duration, audioTrack },
		} = this.props
		const { coverImageItem, maxImageHeight } = this.state

		const isLoaded = playerStatus === PlayerStatus.Playing || playerStatus === PlayerStatus.Pause

		// format time
		const elapsedTime: number = currentTime ? Math.round(currentTime) : 0
		const formattedDuration = duration ? Math.round(duration) : 0
		const remainingTime: number = duration && currentTime ? Math.round(duration - currentTime) : formattedDuration
		const formattedElapsedTime: string = formatTimeMS(elapsedTime)
		const formattedRemainingTime: string = '-' + formatTimeMS(remainingTime)

		const margin = Number(getStyleProperty('--content-margin-horizontal')) || 0

		// transcript
		const hasTranscript = !!audioTrack?.audio?.transcription

		// adjacent audio items (within the same section)
		const prevItem = getAdjacentAudioItem(this.props.currentAudio, -1)
		const nextItem = getAdjacentAudioItem(this.props.currentAudio, 1)
		const hasPrevTrack = !!prevItem
		const hasNextTrack = !!nextItem
		const nextLine = nextItem?.title
			? (strings.formatString(strings.next_audio_track_s, { title: nextItem.title }) as string)
			: undefined

		const coverImage =
			coverImageItem?.file &&
			generateImageUrl(coverImageItem?.file, {
				imageSizePreset: ImageSizePreset.Row,
			})
		const coverImageSrcSet =
			coverImageItem?.file &&
			generateImageSrcSet(coverImageItem?.file, {
				imageSizePreset: ImageSizePreset.Row,
			})

		return (
			<>
				<IonGrid className={classnames('audio-player', 'audio-player-full', hasTranscript && 'has-transcript')}>
					{/* Left pane: cover, title, progress and controls. `display: contents`
				    in portrait so the DOM/layout is identical to before; becomes the
				    real left column only in the landscape two-column rule. */}
					<div className="player-main">
						{coverImage && (
							<IonRow ref={this.onImageRef} className="image-row" style={{ opacity: maxImageHeight ? 1 : 0 }}>
								<div className="image-wrap">
									<img src={coverImage} srcSet={coverImageSrcSet} className="image" alt={getImageAlt(coverImageItem)} />
								</div>
							</IonRow>
						)}

						{coverImageItem?.caption && audioTrack.slideshowEnabled ? (
							<IonRow className="image-caption">
								<div dangerouslySetInnerHTML={{ __html: coverImageItem.caption }} />
							</IonRow>
						) : null}

						<IonRow
							className={classnames('player-row', hasTranscript && 'has-transcript')}
							style={{ marginLeft: margin, marginRight: margin }}
						>
							{audioTrack?.audio?.title && (
								<IonRow className={classnames('title-row', !coverImage && 'no-cover-image')}>
									<Text variant="inherit">{audioTrack.audio.title}</Text>
								</IonRow>
							)}
							{nextLine && (
								<IonRow className="next-up-row">
									<Text variant="inherit">{nextLine}</Text>
								</IonRow>
							)}
							<IonRow className="time-progress-row" dir="ltr">
								<Text variant="inherit" className="time-label">
									{formattedElapsedTime}
								</Text>
								<div className="audio-range-row">
									<AudioSeekBar
										className="audio-seek-bar audio-seek-bar--fullscreen"
										currentTime={currentTime}
										duration={duration}
										disabled={!isLoaded}
										ariaLabel={strings.accessibility_audio_seek}
										onSeek={pct => {
											// Read fresh props at seek time (not the render-time closure) — a
											// release can race a track switch. seekToPercentage no-ops on a
											// missing audio/container.
											const { duration, audioTrack, container } = this.props.currentAudio || {}
											const { audio } = audioTrack || {}
											seekToPercentage(audio?.id, pct, duration, container, audio?.file?.id)
										}}
									/>
								</div>
								<Text variant="inherit" className="time-label">
									{formattedRemainingTime}
								</Text>
							</IonRow>
							<IonRow className={`players-controls ${isRTL() ? 'rtl' : ''}`}>
								{/* Back 10 seconds */}
								{this.getBackButton()}

								{/* Previous track */}
								{this.getTrackStepButton(-1, hasPrevTrack)}

								{/* Play/Pause */}
								{playerStatus === PlayerStatus.Loading ? (
									<Spinner variant="crescent" aria-label={strings.loading} />
								) : (
									this.getPlayPauseButton()
								)}

								{/* Next track */}
								{this.getTrackStepButton(1, hasNextTrack)}

								{/* Forward 10 seconds */}
								{this.getForwardButton()}
							</IonRow>
						</IonRow>
					</div>

					{/* Transcript card — lifted out to be a sibling of `.player-main` so it
				    can become the right column in landscape. In portrait it still sits
				    directly under the controls; the styles keep that spacing identical
				    (see `.player-row.has-transcript` zeroing its bottom padding). */}
					{hasTranscript && (
						<IonRow
							className="transcript-card"
							dir={isRTL() ? 'rtl' : 'ltr'}
							role="button"
							tabIndex={0}
							aria-label={strings.accessibility_open_transcript}
							onClick={this.openTranscriptModal}
							onKeyDown={e => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault()
									this.openTranscriptModal()
								}
							}}
						>
							<Text variant="inherit" className="transcript-card-header">
								{strings.transcript}
							</Text>
							<SyncedTranscript
								variant="card"
								transcription={audioTrack?.audio?.transcription}
								transcriptionFormat={audioTrack?.audio?.transcription_format}
								mediaItemId={audioTrack?.audio?.id}
								currentTime={currentTime}
								isPlaying={playerStatus === PlayerStatus.Playing}
							/>
						</IonRow>
					)}
				</IonGrid>
				{hasTranscript && this.state.showTranscriptModal && (
					<div className="transcript-modal" role="dialog" aria-modal="true">
						<div className="transcript-modal-header">
							<Button
								variant="clear"
								onClick={this.closeTranscriptModal}
								aria-label={strings.accessibility_close_transcript}
							>
								<i className="far fa-chevron-down" />
							</Button>
							<span className="transcript-modal-title">{audioTrack?.audio?.title}</span>
						</div>
						<div className="transcript-modal-body">
							<div className="transcript-modal-label">{strings.transcript}</div>
							<SyncedTranscript
								variant="modal"
								transcription={audioTrack?.audio?.transcription}
								transcriptionFormat={audioTrack?.audio?.transcription_format}
								mediaItemId={audioTrack?.audio?.id}
								currentTime={currentTime}
								isPlaying={playerStatus === PlayerStatus.Playing}
							/>
						</div>
					</div>
				)}
			</>
		)
	}
}
